/**
 * The lost-aircraft search tool.
 *
 * A commander comes here when an aircraft has gone quiet and a decision has to
 * be made about where to send people to look. The panel's whole job is to be
 * trusted, which means it never pretends to more than it knows: it asks what is
 * believed to have happened, shows a ranked set of search areas rather than a
 * single pin, states the radius and the reasoning for each, and draws them to
 * scale on the map.
 *
 * The estimate is only as good as the cause. "Shot down" and "the link dropped
 * and it flew home" lead to different fields, so the commander tells the tool
 * which it is; when they cannot, it defaults to searching the near footprint
 * first, because an aircraft down in the field behind you is the one you find
 * too late.
 */

import { useCallback, useState } from 'react';

import { estimateImpact, type ImpactCause } from '@/api/impact';
import { useImpactStore, type ImpactEstimate, type ImpactHeatmap } from '@/stores/impactStore';
import type { DroneId } from '@/types/domain';

function metres(value: number): string {
  return value >= 1000 ? `${(value / 1000).toFixed(1)} km` : `${Math.round(value)} m`;
}

/**
 * The Monte Carlo containment, stated as the sentence a search is planned
 * against: half the simulated impacts fall inside the inner ring, nine in ten
 * inside the outer. The spread between them is the honesty — a wide gap means
 * the inputs did not pin the outcome down, and the map shows that as a diffuse
 * cloud rather than a confident dot.
 */
function ContainmentCard({ heatmap }: { heatmap: ImpactHeatmap }) {
  const inner = heatmap.contours.find((contour) => contour.fraction === 0.5);
  const outer = heatmap.contours.find((contour) => contour.fraction === 0.9);
  return (
    <div className="well flex flex-col gap-1 rounded-[11px] px-3 py-2.5">
      <div className="flex items-baseline gap-2">
        <span className="label-mono text-[9px] text-[color:var(--friend)]">PROBABLE AREA</span>
        <span className="label-mono ml-auto text-[8px] text-[color:var(--muted-foreground-2)]">
          {heatmap.samples.toLocaleString()} SIMS
        </span>
      </div>
      {inner ? (
        <div className="flex items-baseline gap-2 text-[11px]">
          <span className="text-[color:var(--muted-foreground-2)]">50% WITHIN</span>
          <span className="tnum ml-auto text-[13px]">{metres(inner.radiusM)}</span>
        </div>
      ) : null}
      {outer ? (
        <div className="flex items-baseline gap-2 text-[11px]">
          <span className="text-[color:var(--muted-foreground-2)]">90% WITHIN</span>
          <span className="tnum ml-auto text-[13px]">{metres(outer.radiusM)}</span>
        </div>
      ) : null}
      <p className="text-[9px] leading-snug text-[color:var(--muted-foreground-2)]">
        Simulated {heatmap.samples.toLocaleString()} falls varying sink rate, glide, wind and
        heading. The heat on the map is where they landed.
      </p>
    </div>
  );
}

const CAUSES: Array<{ value: ImpactCause; label: string; hint: string }> = [
  { value: 'unknown', label: 'UNKNOWN', hint: 'Assume it is down nearby, then widen.' },
  { value: 'comms-lost', label: 'COMMS LOST', hint: 'The link dropped; it may have flown on.' },
  { value: 'shot-down', label: 'SHOT DOWN', hint: 'Brought down at the last-known point.' },
  { value: 'impact', label: 'HIT TARGET', hint: 'Flew into the target or the ground.' },
];

const SCENARIO_LABEL: Record<ImpactEstimate['scenario'], string> = {
  loss: 'LOSS FOOTPRINT',
  return: 'RETURN TO LAUNCH',
  continue: 'FLYAWAY',
};

const LIKELIHOOD_TONE: Record<ImpactEstimate['likelihood'], string> = {
  primary: 'var(--danger-foreground)',
  secondary: 'var(--warn)',
  possible: 'var(--muted-foreground-2)',
};

function EstimateCard({ estimate }: { estimate: ImpactEstimate }) {
  const tone = LIKELIHOOD_TONE[estimate.likelihood];
  return (
    <div className="well flex flex-col gap-1 rounded-[11px] px-3 py-2.5">
      <div className="flex items-baseline gap-2">
        <span className="label-mono text-[9px]" style={{ color: tone }}>
          {SCENARIO_LABEL[estimate.scenario]}
        </span>
        <span className="label-mono ml-auto text-[8px]" style={{ color: tone }}>
          {estimate.likelihood.toUpperCase()}
        </span>
      </div>
      <div className="flex items-baseline gap-2 text-[11px]">
        <span className="text-[color:var(--muted-foreground-2)]">SEARCH RADIUS</span>
        <span className="tnum ml-auto text-[13px]" style={{ color: tone }}>
          {estimate.radiusM >= 1000
            ? `${(estimate.radiusM / 1000).toFixed(1)} km`
            : `${Math.round(estimate.radiusM)} m`}
        </span>
      </div>
      <div className="flex items-baseline gap-2 text-[11px]">
        <span className="text-[color:var(--muted-foreground-2)]">CENTRE</span>
        <span className="tnum ml-auto text-[10px]">
          {estimate.lat.toFixed(5)}, {estimate.lon.toFixed(5)}
        </span>
      </div>
      <p className="text-[10px] leading-snug text-[color:var(--muted-foreground-2)]">
        {estimate.rationale}
      </p>
    </div>
  );
}

export function ImpactTab({ droneId }: { droneId: DroneId | null }) {
  const result = useImpactStore((state) => state.result);
  const setResult = useImpactStore((state) => state.setResult);
  const clear = useImpactStore((state) => state.clear);

  const [cause, setCause] = useState<ImpactCause>('unknown');
  const [windSpeed, setWindSpeed] = useState('');
  const [windFrom, setWindFrom] = useState('');
  const [failsafeRtl, setFailsafeRtl] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    if (!droneId) return;
    setBusy(true);
    setError(null);
    const windSpeedMs = windSpeed.trim() === '' ? undefined : Number(windSpeed);
    const outcome = await estimateImpact(droneId, {
      cause,
      failsafeIsRtl: failsafeRtl,
      // Wind is only sent when a speed was entered; a blank field means "we do
      // not know", which the server widens the ring for rather than assuming calm.
      windSpeedMs,
      windFromDeg: windSpeedMs === undefined ? undefined : Number(windFrom || 0),
    });
    setBusy(false);
    if (outcome.ok) {
      setResult(outcome.data);
    } else {
      setError(outcome.error.message);
    }
  }, [droneId, cause, windSpeed, windFrom, failsafeRtl, setResult]);

  if (!droneId) {
    return (
      <div className="well rounded-[11px] px-3 py-2.5">
        <p className="text-[11px] text-[color:var(--muted-foreground-2)]">
          Select the aircraft that went quiet to estimate where it came down.
        </p>
      </div>
    );
  }

  const showFailsafe = cause === 'comms-lost' || cause === 'unknown';
  const forThisDrone = result?.droneId === droneId ? result : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="well flex flex-col gap-2 rounded-[11px] px-3 py-2.5">
        <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
          WHAT HAPPENED
        </span>
        <div className="grid grid-cols-2 gap-1">
          {CAUSES.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setCause(option.value)}
              className="well rounded-[7px] px-2 py-1 label-mono text-[9px]"
              style={
                cause === option.value
                  ? { color: 'var(--foreground)', outline: '1px solid var(--friend)' }
                  : { color: 'var(--muted-foreground-2)' }
              }
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="text-[9px] leading-snug text-[color:var(--muted-foreground-2)]">
          {CAUSES.find((option) => option.value === cause)?.hint}
        </p>

        {showFailsafe ? (
          <label className="flex items-center gap-2 text-[10px]">
            <input
              type="checkbox"
              checked={failsafeRtl}
              onChange={(event) => setFailsafeRtl(event.target.checked)}
            />
            <span className="text-[color:var(--muted-foreground-2)]">
              Failsafe returns home on link loss
            </span>
          </label>
        ) : null}

        <div className="flex items-center gap-1.5">
          <label className="flex flex-1 flex-col gap-0.5">
            <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
              WIND m/s
            </span>
            <input
              value={windSpeed}
              onChange={(event) => setWindSpeed(event.target.value)}
              inputMode="decimal"
              placeholder="—"
              className="well rounded-[7px] px-2 py-1 font-mono text-[11px] outline-none"
            />
          </label>
          <label className="flex flex-1 flex-col gap-0.5">
            <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">
              FROM °
            </span>
            <input
              value={windFrom}
              onChange={(event) => setWindFrom(event.target.value)}
              inputMode="decimal"
              placeholder="—"
              className="well rounded-[7px] px-2 py-1 font-mono text-[11px] outline-none"
            />
          </label>
        </div>

        <button
          type="button"
          disabled={busy}
          onClick={() => void run()}
          className="well rounded-[99px] px-2 py-1.5 label-mono text-[9px] disabled:opacity-40"
          style={{ outline: '1px solid var(--friend)' }}
        >
          {busy ? 'ESTIMATING…' : 'ESTIMATE SEARCH AREA'}
        </button>
        {error ? (
          <p className="text-[10px] leading-snug text-[color:var(--danger-foreground)]">{error}</p>
        ) : null}
      </div>

      {forThisDrone ? (
        <>
          {!forThisDrone.lastKnown.fromSnapshot ? (
            <p className="text-[9px] leading-snug text-[color:var(--warn)]">
              No live telemetry for this aircraft — estimated from its last-known state.
            </p>
          ) : null}
          {forThisDrone.heatmap ? <ContainmentCard heatmap={forThisDrone.heatmap} /> : null}
          {forThisDrone.estimates.map((estimate) => (
            <EstimateCard key={`${estimate.scenario}-${estimate.likelihood}`} estimate={estimate} />
          ))}
          <p className="text-[9px] leading-snug text-[color:var(--muted-foreground-2)]">
            A search aid, not a fix. Rings are where to look, ranked — start with the brightest.
          </p>
          <button
            type="button"
            onClick={clear}
            className="label-mono text-[8px] text-[color:var(--muted-foreground-2)] hover:text-[color:var(--danger-foreground)]"
          >
            CLEAR FROM MAP
          </button>
        </>
      ) : null}
    </div>
  );
}
